import {
  faArrowUpRightFromSquare,
  faCalendar,
  faCamera,
  faChevronDown,
  faChevronUp,
  faClock,
  faDollarSign,
  faGlobe,
  faLocationPin,
  faPhone,
  faUsers,
} from '@fortawesome/duotone-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC, type ReactNode, useState } from 'react';
import './RecommendationCard.scss';

interface Image {
  readonly altText: string;
  readonly uri: string;
}

interface Address {
  readonly formattedAddress: string;
  readonly mapsUrl?: string | null;
}

interface Location {
  readonly name: string;
  readonly mapLink?: string | null;
}

interface PhoneNumber {
  readonly nationalFormat: string;
}

interface Tag {
  readonly className: string;
  readonly label: string;
}

interface Capacity {
  readonly label: string;
  readonly capacity?: number | null;
}

interface Props {
  readonly address?: Address | null;
  readonly capacities?: Capacity[];
  readonly cost?: string | null;
  readonly dateTime?: string | null;
  readonly description?: string | null;
  readonly hoursToday?: string | null;
  readonly image?: Image | null;
  readonly location?: Location | null;
  readonly name: string;
  readonly phoneNumber?: PhoneNumber | null;
  readonly subtitleContent?: ReactNode;
  readonly tags?: readonly Tag[];
  readonly url: string | null;
  readonly virtualTourLink?: string | null;
}

export const RecommendationCard: FC<Props> = ({
  address,
  capacities,
  cost,
  dateTime,
  description,
  hoursToday,
  image,
  location,
  name,
  phoneNumber,
  subtitleContent,
  tags,
  url,
  virtualTourLink,
}) => {
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div className={`card recommendation-card`}>
      <div className={'card-image'}>
        <figure className={'image is-4by3'}>{image?.uri ? <img src={image.uri} alt={image.altText} /> : null}</figure>
      </div>
      <div className={'card-content'}>
        <header className={'card-title block'}>
          <p className={'title is-6'}>
            {url ? (
              <span className={'icon-text'}>
                <a href={url} target={'_blank'} rel={'nofollow'}>
                  <span>{name}</span>
                  <span className={'icon'}>
                    <FontAwesomeIcon icon={faGlobe} size={'sm'} />
                  </span>
                </a>
              </span>
            ) : (
              name
            )}
          </p>
          {subtitleContent ? <p className={'subtitle is-7'}>{subtitleContent}</p> : null}
        </header>
        <div>
          {tags && tags.length > 0 ? (
            <div className={'tags'}>
              {tags.map(({ className, label }, index) => (
                <span key={index} className={`tag ${className}`}>
                  {label}
                </span>
              ))}
            </div>
          ) : null}
          {(address ??
          location ??
          phoneNumber ??
          hoursToday ??
          dateTime ??
          cost ??
          virtualTourLink ??
          (capacities && capacities.length > 0)) ? (
            <ul className={'fa-ul details-list block'}>
              {hoursToday && (
                <li>
                  <span className={'fa-li'}>
                    <FontAwesomeIcon icon={faClock} />
                  </span>
                  <span>{hoursToday}</span>
                </li>
              )}
              {address && (
                <li>
                  <span className={'fa-li'}>
                    <FontAwesomeIcon icon={faLocationPin} />
                  </span>
                  <span>
                    {address.mapsUrl ? (
                      <a href={address.mapsUrl} target={'_blank'} rel={'nofollow'}>
                        {address.formattedAddress}
                      </a>
                    ) : (
                      address.formattedAddress
                    )}
                  </span>
                </li>
              )}
              {location && (
                <li>
                  <span className={'fa-li'}>
                    <FontAwesomeIcon icon={faLocationPin} />
                  </span>
                  <span>
                    {location.mapLink ? (
                      <a href={location.mapLink} target={'_blank'} rel={'nofollow'}>
                        {location.name}
                      </a>
                    ) : (
                      location.name
                    )}
                  </span>
                </li>
              )}
              {virtualTourLink && (
                <li>
                  <span className={'fa-li highlighted'}>
                    <FontAwesomeIcon icon={faCamera} />
                  </span>
                  <span>
                    <a href={virtualTourLink} target={'_blank'} rel={'nofollow'} className={'icon-text'}>
                      <span>Virtual Tour</span>
                      <span className={'icon'}>
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                      </span>
                    </a>
                  </span>
                </li>
              )}
              {capacities && capacities.length > 0 && (
                <li>
                  <span className={'fa-li'}>
                    <FontAwesomeIcon icon={faUsers} />
                  </span>
                  <div className={'capacity-configurations'}>
                    <span className={'capacity-label'}>Capacities</span>
                    {capacities
                      .filter(({ capacity }) => capacity != null && capacity > 0)
                      .map(({ label, capacity }) => (
                        <div key={label} className={'capacity-item'}>
                          <span className={'capacity-text'}>
                            {capacity?.toString()} {label}
                          </span>
                        </div>
                      ))}
                  </div>
                </li>
              )}
              {phoneNumber && (
                <li>
                  <span className={'fa-li'}>
                    <FontAwesomeIcon icon={faPhone} />
                  </span>
                  <span>
                    <a href={`tel:${phoneNumber.nationalFormat}`} target={'_blank'} rel={'nofollow'}>
                      {phoneNumber.nationalFormat}
                    </a>
                  </span>
                </li>
              )}
              {dateTime && (
                <li>
                  <span className={'fa-li'}>
                    <FontAwesomeIcon icon={faCalendar} />
                  </span>
                  <span>{dateTime}</span>
                </li>
              )}
              {cost && (
                <li>
                  <span className={'fa-li'}>
                    <FontAwesomeIcon icon={faDollarSign} />
                  </span>
                  <span>{cost}</span>
                </li>
              )}
            </ul>
          ) : null}
          {description ? (
            <div className={'recommendation-description'}>
              <div className={`content ${collapsed ? 'is-collapsed' : 'is-expanded'}`}>
                <p className={'is-size-7'}>{description}</p>
              </div>
              <button
                className={'button is-small is-primary is-soft is-rounded'}
                onClick={() => {
                  setCollapsed(!collapsed);
                }}
              >
                <span className={'icon-text'}>
                  <span>{collapsed ? 'See more' : 'See less'}</span>
                  <span className={'icon'}>
                    <FontAwesomeIcon icon={collapsed ? faChevronDown : faChevronUp} />
                  </span>
                </span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
RecommendationCard.displayName = 'RecommendationCard';
